import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import { addDays, formatISO, isAfter, isBefore, parseISO, startOfToday } from 'date-fns';
import { AuthContext } from 'authentication/AuthContext';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import WizardLayout from './WizardLayout';
import { initialShipperAllocationState } from './initialState';
import { isOptionEqualToValue } from '../utils';
import {
  useGetBaseProductsQuery,
  useGetOriginsQuery,
  useGetRecentBatchesQuery,
} from '../batchesEndpoints';
import Products1090 from './Products1090';

const pickerPaperStyles = {
  sx: {
    border: 1,
    borderColor: 'graphic.light',
    borderRadius: 0,
    'span.MuiTypography-root': {
      color: 'inherit',
    },
  },
};

const BatchDetails = ({ batchState, setBatchState, onNext, onClose }) => {
  const {
    batchName,
    batchOwner,
    origin,
    isFocus,
    product,
    fuelType,
    oxygenate,
    rvp,
    availableDate,
    sulphurContent,
    component,
    po,
    comments,
  } = batchState;

  const dataCompanyId = R.prop('dataCompanyId', batchOwner);
  const companyCustomerIdName = R.prop('customerIdName', batchOwner);

  const { affiliates = {} } = useContext(AuthContext);
  const batchOwners = affiliates.filter(R.propEq('shipper', true));
  const [isVisible, setIsVisible] = useState(false);
  const dateFieldRef = useRef(null);

  // Proxies setBatchState, but resets Step 2 state along the way
  const updateAndResetShipments = f => {
    setBatchState(R.compose(R.mergeLeft(initialShipperAllocationState), f));
  };

  const {
    data: originsQuery = [],
    isFetching: isFetchingOrigins,
    isSuccess: isOriginsSuccess,
  } = useGetOriginsQuery(
    {
      params: { inventoryOwner: { companyCustomerIdName, dataCompanyId } },
    },
    { skip: R.isNil(batchOwner) }
  );

  const {
    data: productsQuery = [],
    isFetching: isFetchingProducts,
    isSuccess: isProductsSuccess,
  } = useGetBaseProductsQuery({ params: { dataCompanyId } }, { skip: R.isNil(batchOwner) });

  const {
    data: recentBatchQuery = [],
    isFetching: isFetchingBatches,
    isSuccess: isRecentBatchSuccess,
  } = useGetRecentBatchesQuery({
    params: { inventoryOwner: { companyCustomerIdName, dataCompanyId } },
  });

  const [validationState, setValidationState] = useState({
    availableDate: '',
    comments: '',
    po: '',
  });

  const handleBatchOwnerChange = (_, value) => {
    setIsVisible(false);
    updateAndResetShipments(
      R.mergeLeft({
        batchName: null,
        batchOwner: value,
        component: null,
        fuelType: null,
        origin: null,
        oxygenate: null,
        product: null,
        rvp: null,
        sulphurContent: null,
      })
    );
  };

  const handleOriginChange = (_, value) => {
    if (product !== null) setIsVisible(true);
    updateAndResetShipments(R.assoc('origin', value));
  };

  const handleProductChange = (_, value) => {
    updateAndResetShipments(
      R.mergeLeft({
        component: null,
        fuelType: null,
        oxygenate: null,
        product: value,
        rvp: null,
        sulphurContent: null,
      })
    );
  };

  const handleRecentBatchChange = (_, value) => {
    if (product !== null) setIsVisible(true);
    const oxygenateValue = value.oxygenate !== null ? value.oxygenate : 'N/A';
    const componentValue = value.component
      ? { data: value.component, display: value.component }
      : { data: 'N/A', display: 'N/A' };
    const sulphurContentValue = value.sulphurContent
      ? {
          data: value.sulphurContent,
          display: value.sulphurContent,
        }
      : null;
    const fuelTypeValue =
      value.fuelType !== null && value.fuelType !== 'Missing Product Designation'
        ? { data: value.fuelType, display: value.fuelType }
        : null;
    const rvpValue =
      value.rvp !== null
        ? { data: value.rvp, display: value.rvp }
        : { data: 'N/A', display: 'N/A' };
    const productInfo = productsQuery.find(prod => prod.productId === value.productId);
    const productValue =
      value.productId !== null
        ? {
            isGasoline: productInfo ? productInfo.isGasoline : false,
            productId: value.productId,
            productName: value.product,
          }
        : null;
    const originValue =
      value.originId !== null
        ? { isShipper: true, originId: value.originId, originName: value.origin }
        : null;

    updateAndResetShipments(
      R.mergeLeft({
        batchName: value,
        component: componentValue,
        fuelType: fuelTypeValue,
        origin: originValue,
        oxygenate: oxygenateValue,
        product: productValue,
        rvp: rvpValue,
        sulphurContent: sulphurContentValue,
      })
    );
    dateFieldRef.current.focus();
  };

  useEffect(() => {
    if (dateFieldRef.current && isFocus) {
      dateFieldRef.current.focus();
    }
  }, [isFocus]);

  const validateAvailableDate = date => {
    if (R.isNil(date)) {
      setValidationState(R.assoc('availableDate', 'Date required'));
    } else if (date.toString() === 'Invalid Date') {
      setValidationState(R.assoc('availableDate', 'Invalid Date'));
    } else if (isBefore(new Date(date), startOfToday())) {
      setValidationState(R.assoc('availableDate', 'Cannot be in the past'));
    } else if (isAfter(new Date(date), addDays(startOfToday(), 60))) {
      setValidationState(R.assoc('availableDate', 'Cannot be more than 60 days out'));
    } else {
      setValidationState(R.assoc('availableDate', ''));
    }
  };

  const handleAvailableDateChange = value => {
    validateAvailableDate(value);
    const date = R.tryCatch(val => {
      if (val.getUTCFullYear() < 1000) {
        return null;
      }

      return formatISO(val);
    }, R.always(null))(value);

    updateAndResetShipments(R.assoc('availableDate', date));
  };

  const validatePo = R.compose(
    setValidationState,
    R.assoc('po'),
    R.ifElse(R.gte(40), R.always(''), R.always('Cannot exceed 40 characters')),
    R.length
  );

  const handlePoChange = R.compose(
    updateAndResetShipments,
    R.assoc('po'),
    R.tap(validatePo),
    R.path(['target', 'value'])
  );

  const validateComments = R.compose(
    setValidationState,
    R.assoc('comments'),
    R.ifElse(R.gte(255), R.always(''), R.always('Cannot exceed 255 characters')),
    R.length
  );

  useEffect(() => {
    if (batchState.product || batchState.fuelType || batchState.oxygenate || batchState.rvp) {
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  }, [batchState.product, batchState.fuelType, batchState.oxygenate, batchState.rvp]);

  const handleCommentsChange = R.compose(
    updateAndResetShipments,
    R.assoc('comments'),
    R.tap(validateComments),
    R.path(['target', 'value'])
  );

  useEffect(() => {
    if (R.isNil(batchOwner) && batchOwners && batchOwners.length > 0) {
      const [firstOwner] = batchOwners;
      handleBatchOwnerChange('_', firstOwner);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchOwner, batchOwners]);

  const isProductGasoline = R.propEq('isGasoline', true, product);
  const isProductDistillate = R.propEq('isGasoline', false, product);

  const fuelTypes = ['Summer CBOB', 'Summer CG', 'Summer RBOB', 'Summer RFG'];
  const isFuelTypeSummerCbobOrCg = fuelTypes.includes(R.prop('data', fuelType));

  const isInvalid = [
    R.isNil(batchOwner),
    R.isNil(origin),
    R.isNil(product),
    R.isNil(fuelType),
    R.and(isProductDistillate, R.isNil(sulphurContent)),
    R.and(isProductGasoline, R.isNil(oxygenate)),
    isFuelTypeSummerCbobOrCg ? R.isNil(rvp) : false,
    R.and(isProductGasoline, R.isNil(component)),
    R.isNil(availableDate),
    R.not(R.isEmpty(validationState.availableDate)),
    R.not(R.isEmpty(validationState.po)),
    R.not(R.isEmpty(validationState.comments)),
  ].includes(true);

  const buttons = (
    <>
      <Button onClick={onClose} sx={{ width: 104 }} variant="outlined">
        Back
      </Button>
      <Button disabled={isInvalid} onClick={onNext} sx={{ width: 104 }}>
        Continue
      </Button>
    </>
  );
  const errorValidate = !R.isEmpty(validationState.availableDate);
  const helperTextValidate = validationState.availableDate;

  return (
    <WizardLayout buttons={buttons} onClose={onClose}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Card elevation={1} sx={{ maxWidth: '1470px', pt: 2, px: 2, width: '100%' }}>
          <CardHeader
            slotProps={{
              title: { sx: { color: 'primary.dark' }, variant: 'h5' },
            }}
            sx={{ pl: 1 }}
            title="Batch Details"
          />
          <CardContent sx={{ pt: 0, px: 1 }}>
            {R.gt(batchOwners.length, 1) && (
              <Autocomplete
                disableClearable
                getOptionLabel={R.prop('customerNameLong')}
                isOptionEqualToValue={(option, value) =>
                  R.and(
                    R.eqProps('dataCompanyId', option, value),
                    R.eqProps('customerIdName', option, value)
                  )
                }
                onChange={handleBatchOwnerChange}
                options={batchOwners}
                renderInput={inputParams => <TextField {...inputParams} label="Batch Owner" />}
                value={batchOwner}
              />
            )}
            <Stack sx={{ my: 4 }}>
              <Typography sx={{ color: 'text.primary' }} variant="label">
                Copy Recent Batch
              </Typography>
              <Typography sx={{ color: 'text.secondary', mb: 1.5, mt: 1 }} variant="body2">
                Select a previous batch to copy Origin Details and 1090 Product Designation.
              </Typography>
              <Autocomplete
                disableClearable
                disabled={!isRecentBatchSuccess}
                getOptionLabel={R.prop('batchName')}
                loading={isFetchingBatches}
                onChange={handleRecentBatchChange}
                options={recentBatchQuery}
                renderInput={inputParams => <TextField {...inputParams} label="Recent Batch" />}
                value={batchName}
              />
            </Stack>
            <Stack spacing={2} sx={{ my: 4 }}>
              <Typography sx={{ color: 'text.primary' }} variant="label">
                {' '}
                Origin Details{' '}
              </Typography>
              <Autocomplete
                disableClearable
                disabled={!isOriginsSuccess}
                getOptionDisabled={R.propEq('isShipper', false)}
                getOptionLabel={R.prop('originName')}
                isOptionEqualToValue={isOptionEqualToValue('originId')}
                loading={isFetchingOrigins}
                onChange={handleOriginChange}
                options={originsQuery}
                renderInput={inputParams => <TextField {...inputParams} label="Origin" />}
                value={origin}
              />

              <Autocomplete
                disableClearable
                disabled={!isProductsSuccess}
                getOptionLabel={R.prop('productName')}
                isOptionEqualToValue={isOptionEqualToValue('productId')}
                loading={isFetchingProducts}
                onChange={handleProductChange}
                options={productsQuery}
                renderInput={inputParams => <TextField {...inputParams} label="Product" />}
                value={product}
              />

              <DatePicker
                inputRef={dateFieldRef}
                label="Available Date"
                maxDate={addDays(startOfToday(), 60)}
                minDate={startOfToday()}
                onChange={handleAvailableDateChange}
                slotProps={{
                  desktopPaper: pickerPaperStyles,
                  textField: {
                    error: errorValidate,
                    helperText: helperTextValidate,
                  },
                }}
                value={parseISO(availableDate)}
              />

              <TextField
                error={!R.isEmpty(validationState.po)}
                fullWidth
                helperText={validationState.po || 'Optional'}
                label="Purchase Order"
                onChange={handlePoChange}
                value={po}
              />

              <TextField
                error={!R.isEmpty(validationState.comments)}
                helperText={validationState.comments || 'Optional'}
                label="Comment"
                multiline
                onChange={handleCommentsChange}
                rows={3}
                value={comments}
              />
            </Stack>
            {isVisible && (
              <Products1090
                batchState={batchState}
                updateAndResetShipments={updateAndResetShipments}
              />
            )}
          </CardContent>
        </Card>
      </LocalizationProvider>
    </WizardLayout>
  );
};

BatchDetails.propTypes = {
  batchState: PropTypes.shape({
    availableDate: PropTypes.string,
    batchName: PropTypes.string,
    batchOwner: PropTypes.shape({}),
    comments: PropTypes.string,
    component: PropTypes.shape({}),
    fuelType: PropTypes.shape({}),
    isFocus: PropTypes.bool,
    origin: PropTypes.shape({}),
    oxygenate: PropTypes.string,
    po: PropTypes.string,
    product: PropTypes.shape({
      isGasoline: PropTypes.bool,
    }),
    rvp: PropTypes.shape({}),
    sulphurContent: PropTypes.shape({}),
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  setBatchState: PropTypes.func.isRequired,
};

export default BatchDetails;
